import { useEffect, useState, useMemo } from "react";
import { fetchJerarquia, fetchGlobalJerarquia } from "../lib/api";
import { useSnapshot } from "../contexts/SnapshotContext";
import { formatEUR } from "../lib/format";
import { RankBadge } from "../components/RankBadge";
import { CaretRight, CaretDown, UsersThree } from "@phosphor-icons/react";

function flatten(roots, expanded) {
  const out = [];
  const walk = (node, depth) => {
    out.push({ node, depth });
    const isOpen = expanded[node.id_consultor] !== false && (depth < 1 || expanded[node.id_consultor] === true);
    if (isOpen && node.children && node.children.length) {
      const sorted = [...node.children].sort(
        (a, b) =>
          (b.vendas_equipa || 0) - (a.vendas_equipa || 0) ||
          (b.vendas_pessoais || 0) - (a.vendas_pessoais || 0)
      );
      sorted.forEach((c) => walk(c, depth + 1));
    }
  };
  roots.forEach((r) => walk(r, 0));
  return out;
}

export default function Jerarquia() {
  const { selected, isGlobal, selectedPais } = useSnapshot();
  const [data, setData] = useState({ tree: [] });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    setLoading(true);
    const req = isGlobal
      ? fetchGlobalJerarquia(selectedPais)
      : fetchJerarquia(selected?.id);
    req.then((d) => {
      setData(d);
      setLoading(false);
    });
  }, [selected?.id, isGlobal, selectedPais]);

  const isNational = !!data.is_national;
  const flat = useMemo(() => flatten(data.tree || [], expanded), [data, expanded]);

  const toggle = (id, currentlyOpen) => {
    setExpanded((prev) => ({ ...prev, [id]: !currentlyOpen }));
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="jerarquia-page">
      <header>
        <p className="text-xs tracking-widest uppercase text-[#736F68] font-display font-semibold">
          Estructura
        </p>
        <h1 className="mt-1 text-3xl md:text-4xl font-semibold font-display text-[#2D2A26]">
          {isNational ? "Organigrama Nacional" : "Jerarquía del equipo"}
        </h1>
        <p className="mt-1 text-[#736F68] text-sm">
          {isNational
            ? "Directora Nacional → Coordinadoras de Área → equipo de cada coordinadora."
            : "Niveles de tu organización. Pulsa para expandir."}
        </p>
      </header>

      <div className="bg-white border border-[#E5E0D8] rounded-xl py-3 px-2">
        {loading ? (
          <p className="p-6 text-[#736F68]">Cargando…</p>
        ) : flat.length === 0 ? (
          <p className="p-6 text-[#736F68]">Sin datos.</p>
        ) : (
          flat.map(({ node, depth }) => {
            const hasChildren = node.children && node.children.length > 0;
            const isOpen = expanded[node.id_consultor] !== false && (depth < 1 || expanded[node.id_consultor] === true);
            return (
              <div
                key={`${node.nome}-${node.id_consultor}-${depth}`}
                className="group flex items-center gap-3 py-2.5 px-3 rounded-lg transition-colors hover:bg-[#F2EFEA]"
                style={{ paddingLeft: `${depth * 18 + 12}px` }}
                data-testid={`tree-node-${node.id_consultor}`}
              >
                <button
                  onClick={() => hasChildren && toggle(node.id_consultor, isOpen)}
                  className={`w-5 h-5 flex items-center justify-center text-[#736F68] ${hasChildren ? "cursor-pointer" : "opacity-0"}`}
                >
                  {hasChildren && (isOpen ? <CaretDown size={14} /> : <CaretRight size={14} />)}
                </button>
                <div className="flex-1 min-w-0 flex items-center gap-3">
                  <p className="font-medium text-[#2D2A26] text-sm truncate">{node.nome}</p>
                  <RankBadge rank={node.titulo || node.rank_qualifying} />
                  {hasChildren && (
                    <span className="text-[10px] text-[#736F68] bg-[#F9F8F6] border border-[#E5E0D8] px-1.5 py-0.5 rounded inline-flex items-center gap-1" title={`${node.descendants_count} descendientes en total`}>
                      <UsersThree size={10} />
                      {node.children.length}
                      {node.descendants_count > node.children.length && (
                        <span className="text-[#C57B67]">/{node.descendants_count}</span>
                      )}
                    </span>
                  )}
                </div>
                <div className="hidden md:flex items-center gap-6 text-xs text-[#736F68]">
                  <span className="font-mono">P: {formatEUR(node.vendas_pessoais)}</span>
                  <span className="font-mono text-[#8A9A86] font-semibold" title="Suma de ventas personales de todas las descendientes (incluyéndola a ella)">
                    Equipo: {formatEUR(node.team_total_calc ?? node.vendas_equipa)}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}